import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardBody, Typography, Collapse } from "@material-tailwind/react";
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { Chart, registerables } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { format, subDays, startOfWeek } from 'date-fns';
import axiosInstance from '../../../services/api/axios';
import { useUser } from '../../../context/UserContext';
import { ChevronDown, ChevronUp } from 'lucide-react';
import SalesDetails from './SalesDetails';
Chart.register(...registerables);

const barColors = ['rgba(255, 99, 132, 0.8)', 'rgba(54, 162, 235, 0.8)', 'rgba(255, 205, 86, 0.8)', 'rgba(75, 192, 192, 0.8)', 'rgba(153, 102, 255, 0.2)'];

function SalesSummary() {
    const [gridApi, setGridApi] = useState(null);
    const [showTable, setShowTable] = useState(false);
    const [apiData, setApiData] = useState([]);
    const [loading, setLoading] = useState(false);
    const { userInfo } = useUser();
    const customerId = userInfo.loginIdentifierDetails.customer.customerId;
    const [dateFilters, setDateFilters] = useState({
        startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
        endDate: format(new Date(), 'yyyy-MM-dd'),
    });

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.get(
                    `/customer/sales/performance?customer_id=${customerId}&start_date=${dateFilters.startDate}&end-date=${dateFilters.endDate}`
                );
                setApiData(response);
                console.log("performance res", response);
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [dateFilters, customerId]);

    const transformedData = useMemo(() => {
        if (!apiData || apiData.length === 0) return [];

        const allProjects = new Set();

        apiData.forEach(item => {
            item.performance.forEach(sale => {
                if (sale.project?.projectName) {
                    allProjects.add(sale.project.projectName);
                }
            });
        });

        const projectNames = Array.from(allProjects);

        return projectNames.map(projectName => {
            const projectSalesData = [];

            apiData.forEach(item => {
                item.performance.forEach(sale => {
                    if (sale.project?.projectName === projectName) {
                        projectSalesData.push({
                            salesperson: item.entity.persona === "STAFF" ? item.entity.name : item.entity.username,
                            totalAmount: sale.cumulativeBasePrice,
                            noOfSales: sale.salesCount,
                            saleDate: sale.saleDate ? new Date(sale.saleDate) : new Date(),
                        });
                    }
                });
            });

            return {
                projectName: projectName,
                salesData: projectSalesData,
            };
        });
    }, [apiData]);

    const columnDefs = useMemo(() => [
        { headerName: "Salesperson", field: "salesperson", sortable: true, filter: true },
        { headerName: "Sales", field: "noOfSales", sortable: true, filter: true },
        { headerName: "Amount", field: "totalAmount", sortable: true, filter: true },
    ], []);

    const onGridReady = (params) => {
        setGridApi(params.api);
    };

    const generateChartData = (project, chartType) => {
        if (!project) return {};

        const aggregatedSales = project.salesData.reduce((acc, sale) => {
            const salesperson = sale.salesperson;
            if (!acc[salesperson]) {
                acc[salesperson] = {
                    salesperson: salesperson,
                    noOfSales: 0,
                    totalAmount: 0,
                };
            }
            acc[salesperson].noOfSales += sale.noOfSales;
            acc[salesperson].totalAmount += sale.totalAmount;
            return acc;
        }, {});

        const labels = Object.keys(aggregatedSales);
        const data = labels.map(salesperson =>
            chartType === 'properties'
                ? aggregatedSales[salesperson].noOfSales
                : aggregatedSales[salesperson].totalAmount
        );

        const chartOptions = {
            indexAxis: 'y',
            scales: {
                x: {
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: chartType === 'properties' ? 'Number of Properties Sold' : 'Total Amount Earned',
                    },
                    ticks: {
                        callback: function (value, index, values) {
                            return chartType === 'properties' ? value : value / 1000000 + 'M';
                        }
                    },
                },
                y: {
                    title: {
                        display: true,
                        text: 'Salesperson',
                    },
                },
            },
            plugins: {
                legend: {
                    display: false
                }
            }
        };

        return {
            labels: labels,
            datasets: [
                {
                    label: chartType === 'properties' ? 'Properties Sold' : 'Total Amount Earned',
                    data: data,
                    backgroundColor: chartType === 'properties' ? barColors[0] : 'rgba(75, 192, 192, 0.8)',
                    borderColor: chartType === 'properties' ? 'rgba(54, 162, 235, 1)' : 'rgba(75, 192, 192, 1)',
                    borderWidth: 1,
                },
            ],
            options: chartOptions
        };
    };


    const handleDateChange = (e) => {
        e.stopPropagation();
        setDateFilters({
            ...dateFilters,
            [e.target.name]: e.target.value,
        });
    };

    const [expandedProjects, setExpandedProjects] = useState({});

    const toggleProject = (projectName) => {
        setExpandedProjects(prevState => ({
            ...prevState,
            [projectName]: !prevState[projectName]
        }));
    };

    const handleSwitchChange = (e) => {
        e.stopPropagation();
        setShowTable(!showTable);
    };

    return (
        <div className="container mx-auto">
            <div className='flex justify-between items-center'>
                {/* Conditional Date Inputs - Placed on the left when is showing Table */}
                 <div className={`flex items-center  ${showTable ? 'hidden' : 'order-1 '}`}>
                    <input
                        type="date"
                        name="startDate"
                        value={dateFilters.startDate || ""}
                        onChange={handleDateChange}
                        className="p-2.5 border border-gray-300 rounded-md text-sm"
                    />
                    <input
                        type="date"
                        name="endDate"
                        value={dateFilters.endDate || ""}
                        onChange={handleDateChange}
                        className="p-2.5 border border-gray-300 rounded-md text-sm ml-2"
                    />
                </div>

                {/* Show Table Switch - Always on the right, flex-end */}
                <div className="flex items-center space-x-2  order-2 ml-auto">
                    <Typography variant="small" color="gray">
                        Show Table
                    </Typography>
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            value=""
                            className="sr-only peer"
                            checked={showTable}
                            onChange={handleSwitchChange}
                            onClick={(e) => e.stopPropagation()}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-600"></div>
                    </label>
                </div>

            </div>

            {/* Conditional Rendering based on showTable */}
            {loading ? (
                <Typography>Loading...</Typography>
            ) : (
                !showTable ? (
                    // GRAPH VIEW
                    <div className="flex flex-col gap-4 py-6">
                        {transformedData.map((project, index) => {
                            const chartDataProperties = generateChartData(project, 'properties');
                            const chartDataAmount = generateChartData(project, 'amount');
                            const isExpanded = expandedProjects[project.projectName] || false;

                            return (
                                <Card key={index} >
                                    <CardBody>
                                        <div
                                            className="flex justify-between items-center cursor-pointer"
                                            onClick={() => toggleProject(project.projectName)}
                                        >
                                            <h2 className="lg:text-xl font-bold text-base m-4">
                                                {project.projectName} Performance
                                            </h2>
                                            {isExpanded ? (
                                                <ChevronUp className="h-6 w-6" />
                                            ) : (
                                                <ChevronDown className="h-6 w-6" />
                                            )}
                                        </div>
                                        <Collapse open={isExpanded}>
                                            <div className="flex gap-4 py-4 m-4">
                                                <div className="w-1/2">
                                                    <Typography variant="h6" className="mb-2">Properties Sold</Typography>
                                                    <div style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}>
                                                        <Bar data={chartDataProperties} options={chartDataProperties?.options} />
                                                    </div>
                                                </div>

                                                <div className="w-1/2">
                                                    <Typography variant="h6" className="mb-2">Amount Earned</Typography>
                                                    <div style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}>
                                                        <Bar data={chartDataAmount} options={chartDataAmount?.options} />
                                                    </div>
                                                </div>
                                            </div>
                                        </Collapse>
                                    </CardBody>
                                </Card>
                            );
                        })}
                    </div>
                ) : (
                    <SalesDetails/>
                )
            )}
        </div>
    );
}

export default SalesSummary;